// THE ASSEMBLY SECTION of the selection panel: what the selected parents
// are MADE OF, grouped by part Category/Family/Type.
//
// The owner asked for this so a category can be derived from another
// category's contents - "track the beam clamps which will be
// automatically merged with the hangers". Selecting Hangers lists their
// parts; a button turns one part group into its own tracked category.
//
// WHERE THE NUMBERS COME FROM, and why none of this walks parts: the
// server sends them, already grouped, the same way SelectionDetails gets
// everything else. The panel never reduces over records in the browser -
// that path holds 1.5 GB of JSON on SpaceX. The server in turn only READS
// the numbers Tom's exporter wrote on the Assembly object; nothing
// anywhere reconstructs a part from an id. That is the owner's rule.
//
// ABSENT IS NOT ZERO, AND THIS FILE IS WHERE THAT IS ENFORCED. Tom's
// group sums are ALWAYS present and may be 0.0, while a part carries a
// measure only when it is non-zero - so a group sum of 0.0 means every
// part LACKED the parameter, not that they measured zero. The server is
// asked to keep 0 out of `measures` entirely; this file treats anything
// that still arrives as absent, because `compact()` renders 0 as an en
// dash, which would look exactly like a missing value while claiming to
// be a measurement.

// MARK'S CONTRACT, read from his CODE (utils/derivedCategories.js
// groupsFor) rather than from either of his two messages - they
// described different shapes and the second superseded the first. The
// row is:
//   { partCategory, partFamily, partType, parentCount, totalCount,
//     rows, perParent, measures[], amounts{}, without{} }
//
// `measures` lists only what GENUINELY HAS A VALUE; a group sum of 0.0
// produces no entry, because Tom always writes the sum and 0.0 means
// every part LACKED the parameter rather than measured zero. This is
// the same convention the rows above this section already use, which
// is why it is the right one: one meaning, one shape, in one file.
//
// The zero matters more than it looks: compact(0) renders an en dash,
// so a zero reaching the screen would be indistinguishable from a
// missing value while claiming to be a measurement - the owner's rule
// looking satisfied while broken.
const MEASURE_LABEL = { length: "length", area: "area", volume: "volume" };
const MEASURE_ORDER = ["length", "area", "volume"];

export function amountOf(group, key) {
  if (!group) return null;
  const listed = Array.isArray(group.measures) && group.measures.includes(key);
  if (!listed) return null;
  const v = Number((group.amounts || {})[key]);
  if (!Number.isFinite(v) || v === 0) return null;
  return v;
}

// `without` carries how many rows of the group had no value for a
// measure, so a PARTIAL sum can say so instead of reading as a
// complete one.
export function missingCountOf(group, key) {
  if (!group || !group.without) return 0;
  const n = Number(group.without[key]);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

// The units come from the BUILD that wrote the numbers, not from the
// page's own preference - otherwise a metric project would be labelled
// in feet by a panel that never read the export.
// Mark sends the unit as a CODE - sqft, cuft - because that is what
// survives a database round trip. The rest of this panel shows ft2 and
// ft3 as proper symbols, so the codes are mapped here rather than at
// four call sites. Anything unrecognised is passed through untouched:
// a unit we have not met should appear as the export wrote it, not
// vanish.
const UNIT_SYMBOL = {
  sqft: "ft\u00B2", cuft: "ft\u00B3", ft2: "ft\u00B2", ft3: "ft\u00B3",
  sqm: "m\u00B2", cum: "m\u00B3", m2: "m\u00B2", m3: "m\u00B3",
};

export function unitSymbol(u) {
  if (!u) return "";
  return UNIT_SYMBOL[String(u).toLowerCase()] || u;
}

export function unitsOf(payload) {
  const raw = (payload && payload.units) || {};
  const out = {};
  for (const k of Object.keys(raw)) out[k] = unitSymbol(raw[k]);
  return out;
}

// "12 of 40 selected have parts" is coverage; this is the other kind of
// incompleteness - the server stopped early.
export function truncatedText(payload) {
  if (!payload || !payload.truncated) return null;
  return "Too many parts to list them all - showing the largest groups.";
}

// Which measure columns the table should show at all: only those some
// group actually has. A column of nothing but admissions is noise.
export function measureColumnsOf(groups) {
  const rows = groups || [];
  return MEASURE_ORDER.filter((k) => rows.some((g) => amountOf(g, k) !== null));
}

// The sentence the owner asked for, in place of a zero.
export function absentText(key) {
  return `no ${MEASURE_LABEL[key] || key} in the model`;
}

// Said ONCE, above the table, when the parts carry no measure at all -
// which is the normal case for the hangers this was built for, since
// Electrical Fixtures carries no length, area or volume.
export function noMeasuresNote(groups) {
  const rows = groups || [];
  if (!rows.length) return null;
  if (measureColumnsOf(rows).length) return null;
  return "These parts carry no length, area or volume in the model - only counts.";
}

// Most-numerous first: the panel exists to find the part worth tracking,
// and that is almost always the one there are most of. Tom sorts his
// Groups by family for a stable file; a person reading a screen wants
// the big one at the top. Ties fall back to his order so the list does
// not shuffle between reads.
export function sortGroups(groups) {
  return (groups || []).slice().sort((a, b) => {
    const d = (Number(b.totalCount) || 0) - (Number(a.totalCount) || 0);
    if (d) return d;
    return `${a.partFamily || ""}${a.partType || ""}`.localeCompare(`${b.partFamily || ""}${b.partType || ""}`);
  });
}

// A stable identity for a part group, used as the React key and as what
// the Track-as-subcategory button hands over.
export function groupKey(g) {
  return [g.partCategory || "", g.partFamily || "", g.partType || ""].join("\u0000");
}

// "Beam Clamps (from Hangers)" - the default name the owner used.
export function defaultSubcategoryName(group, sourceCategory) {
  const part = [group.partFamily, group.partType].filter(Boolean).join(" ").trim();
  const from = (sourceCategory || "").trim();
  if (!part) return from ? `Parts (from ${from})` : "Parts";
  return from ? `${part} (from ${from})` : part;
}

// How many of the selected parents actually have parts. Saying "12 of 40
// selected" is the difference between "these are the parts" and "these
// are the parts of the ones that have any".
export function coverageText(payload) {
  const withA = Number(payload && payload.parentsWithAssembly);
  const total = Number(payload && payload.parentsTotal);
  if (!Number.isFinite(withA) || !Number.isFinite(total) || !total) return null;
  if (withA === total) return null;
  return `${withA.toLocaleString()} of ${total.toLocaleString()} selected have parts`;
}

export const __measures = { MEASURE_ORDER, MEASURE_LABEL };

// THE MOVEMENT A DERIVED CATEGORY WOULD CAUSE, from Mark's dry run
// (POST derived-categories with dryRun:true, f543bfb2). It writes
// nothing and refuses what a real create would refuse, through the SAME
// code path - so the preview cannot be computed differently from the
// thing it previews.
//
// SHOWN BEFORE THE CLICK, NEVER AFTER. He first put these two numbers on
// the create response, which arrives once the percentage has already
// moved: a receipt, not a warning. A board percentage that drops because
// someone added a category is reported as a bug by whoever sees it next,
// unless the person who caused it knew in advance.
export function progressMoveText(preview) {
  if (!preview) return null;
  const a = preview.progressBefore;
  const b = preview.progressAfter;
  if (!a || !b) return null;
  // Number(null) is 0 AND FINITE, so a bare pair of nulls would have
  // rendered as "stays at 0.0%" - a confident sentence about a number
  // nobody computed. Reject the absent value before converting it, not
  // after. (Caught by its own test, 2026-09-24.)
  if (a.percentProgress === null || a.percentProgress === undefined) return null;
  if (b.percentProgress === null || b.percentProgress === undefined) return null;
  const from = Number(a.percentProgress);
  const to = Number(b.percentProgress);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
  const f = `${from.toFixed(1)}%`;
  const t = `${to.toFixed(1)}%`;
  // SAY IT MOVES NOWHERE rather than printing "78.2% to 78.2%" and
  // leaving the person to compare two identical numbers.
  if (f === t) return `Project progress stays at ${f}.`;
  return `Project progress moves from ${f} to ${t}.`;
}

// Mark's dry run refuses the empty definition and an overlap with an
// existing category, with the message a real create would give - so the
// refusal is readable before the person types the name a second time.
export function refusalOf(preview) {
  if (!preview) return null;
  // `previewUnavailable` is the server saying it CANNOT work the effect
  // out - a project with no published model, say - and it carries the
  // reason. Two nulls with no sentence would read as "this changes
  // nothing", which is the same inversion as a 0.0 rendering as a
  // measurement. (Mark fixed that end in 7c112427.)
  const msg = preview.previewUnavailable || preview.err || preview.error || preview.refused;
  return typeof msg === "string" && msg ? msg : null;
}

// ONE SHAPE END TO END. The preview and the create are handed the SAME
// object, and the page adds dryRun:true and POSTs it verbatim - no
// translation layer anywhere, because a translation layer is a place
// for the two to drift. The day this dialog gains a formula, a page
// that rebuilt the body from three fields would preview one definition
// and create another, and the disagreement we promised to surface as a
// bug would be the thing we had built. (Mark, 2026-09-24: same code
// path or it is theatre - applied to the seam, not the arithmetic.)
//
// No formula is collected yet, so none is sent; the server defaults
// factor 1, perParent 0, waste 0. What matters is that CREATE sends
// exactly what PREVIEW sent, whatever that is.
export function candidateOf({ group, name, measure, sourceCategory, formula }) {
  if (!group) return null;
  return {
    ...(name ? { name } : {}),
    source: { category: sourceCategory || "", family: "", type: "" },
    part: {
      category: group.partCategory || "",
      family: group.partFamily || "",
      type: group.partType || "",
    },
    measure: measure || "count",
    ...(formula ? { formula } : {}),
  };
}

// THE ONE LINE THE SECTION SHOWS WHILE IT IS CLOSED.
//
// It ships COLLAPSED (Paul + Mark, 2026-09-25): the section keeps the
// position the panel's narrowing gives it, while the Elements list -
// where the owner ASSIGNS STATUS, constantly - stays above the fold.
// Ordering by how often a thing is used beats ordering by how the
// concepts nest; collapsing gets both without either of us moving the
// other's code.
//
// The closed line still has to SAY SOMETHING TRUE, or it is a
// disclosure triangle nobody opens. It carries the two numbers that
// tell you whether opening it is worth the click.
export function collapsedSummary(payload) {
  const groups = (payload && payload.groups) || [];
  if (!groups.length) return null;
  let parts = 0;
  for (const g of groups) parts += Number(g.totalCount) || 0;
  const gLabel = `${groups.length.toLocaleString()} part ${groups.length === 1 ? 'group' : 'groups'}`;
  if (!parts) return gLabel;
  return `${gLabel}, ${parts.toLocaleString()} ${parts === 1 ? 'part' : 'parts'}`;
}
